import {
  ChatStateContext,
  useChatState as useSharedChatState,
  useSafeChatState as useSharedSafeChatState,
} from '@github-ui/copilot-chat-contexts/ChatStateContext'
import {CopilotSpaceSelectionProvider} from '@github-ui/copilot-chat-contexts/CopilotSpaceSelectionContext'
import {
  ObservableChatStateProvider,
  useObservableChatStateContext,
} from '@github-ui/copilot-chat-contexts/ObservableChatContext'
import {copilotLocalStorage} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import {buildCopilotChatInitialState} from '@github-ui/copilot-chat-state/copilot-chat-initial-state'
import type {CopilotChatAction, CopilotChatState, Dispatcher} from '@github-ui/copilot-chat-state/copilot-chat-reducer'
import {copilotChatReducer} from '@github-ui/copilot-chat-state/copilot-chat-reducer'
import type {
  CopilotChatMessage,
  CopilotChatMode,
  CopilotChatOrg,
  CopilotChatPayload,
  CopilotChatReference,
  CopilotChatRepo,
  CopilotSpace,
} from '@github-ui/copilot-chat-types'
import {EntitlementProvider} from '@github-ui/copilot-entitlement/EntitlementContext'
import {ObservableValue} from '@github-ui/observable'
import {useDerivedObservable, useObservableValueRef, useObservedState} from '@github-ui/react-observable'
import type {PropsWithChildren, ReactNode} from 'react'
import {createContext, use, useCallback, useEffect, useLayoutEffect, useReducer, useRef} from 'react'

import {useRestoredChatState, useSaveChatState} from '../hooks/use-restore-chat-state'
import {announceReferenceChange} from './copilot-chat-announcements'
import {emitChatTelemetry} from './copilot-chat-telemetry'
import {CopilotChatManagerProvider} from './CopilotChatManagerContext'

/** Stable stand-in so the safe lens can subscribe unconditionally when no chat provider is mounted. */
const emptyChatStateObservable = new ObservableValue<CopilotChatState | undefined>(undefined)

export function useChatState(): CopilotChatState {
  return useSharedChatState()
}

export function useSafeChatState(): CopilotChatState | undefined {
  return useSharedSafeChatState()
}

const CopilotChatDispatchContext = createContext<React.Dispatch<CopilotChatAction> | null>(null)

export interface CopilotChatProviderProps {
  topic?: CopilotChatRepo
  threadId: string | null
  refs: CopilotChatReference[]
  selectedReference?: CopilotChatReference | null
  mode: CopilotChatMode
  ssoOrganizations: CopilotChatOrg[]
  chatIsOpen?: boolean
  deferEntitlementUntilOpen?: boolean
  chatIsVisible?: boolean
  chatVisibleSettingPath?: string
  customCopilots?: CopilotSpace[]
  messages?: CopilotChatMessage[]
  testReducerState?: CopilotChatState
  realIp?: string
  currentView?: 'thread' | 'list'
  copilotChatPayload: CopilotChatPayload
  /** Disable only when this compatibility provider wraps a non-V1 runtime such as AHP quick chat. */
  enableV1CitationMarkerSanitization?: boolean
  /** Prevent descendants from connecting an AHP relay until the initial instruction snapshot is published. */
  waitForInitialInstructionRules?: boolean
  instructionRulesLoadingFallback?: ReactNode
}

export function CopilotChatProvider({
  children,
  topic,
  threadId,
  refs,
  selectedReference,
  mode,
  ssoOrganizations,
  chatIsOpen,
  deferEntitlementUntilOpen = false,
  chatIsVisible,
  chatVisibleSettingPath,
  customCopilots,
  messages,
  testReducerState,
  realIp,
  currentView,
  copilotChatPayload,
  enableV1CitationMarkerSanitization = true,
  waitForInitialInstructionRules = false,
  instructionRulesLoadingFallback,
}: PropsWithChildren<CopilotChatProviderProps>) {
  const restoredChatState = useRestoredChatState(threadId, mode)
  const initialState = buildCopilotChatInitialState({
    topic,
    threadId,
    refs,
    selectedReference,
    mode,
    ssoOrganizations,
    chatIsOpen,
    chatIsVisible,
    chatVisibleSettingPath,
    customCopilots,
    messages,
    testReducerState,
    currentView,
    copilotChatPayload,
    restoredMessages: restoredChatState.restoredMessages,
    restoredThreadTitle: restoredChatState.restoredThreadTitle,
  })

  const [state, baseDispatch] = useReducer(copilotChatReducer, initialState)
  // Track the latest committed state so the dispatch boundary can read the pre-dispatch state
  // when emitting side effects (announcements, telemetry) without making them depend on `state`.
  const stateRef = useRef(state)
  useLayoutEffect(() => {
    stateRef.current = state
  }, [state])
  // Screen-reader announcements and analytics are side effects, so they run here at the dispatch
  // boundary rather than inside the (pure) reducer. They are best-effort and must never block the
  // state update, so a throw in either one is swallowed and the reducer dispatch still runs.
  const dispatch = useCallback<Dispatcher>(
    action => {
      try {
        announceReferenceChange(action)
      } catch {
        // best-effort: a failed announcement must not block the state update
      }
      try {
        emitChatTelemetry(stateRef.current, action)
      } catch {
        // best-effort: failed analytics must not block the state update
      }
      baseDispatch(action)
    },
    [baseDispatch],
  )
  useSaveChatState(state.messages, state.selectedThreadID, state.threads, state.mode)

  useEffect(() => {
    dispatch({
      type: 'IMAGE_UPLOAD_PREVIEW_POLICY_UPDATED',
      enabled: copilotChatPayload.imageUploadPreviewPolicyEnabled,
    })
  }, [copilotChatPayload.imageUploadPreviewPolicyEnabled, dispatch])

  useEffect(
    function saveCurrentReferences() {
      if (!state.chatIsOpen) return

      const referencesWithoutCurrentRepo = state.currentReferences.filter(
        r => r.type !== 'repository' || r.id !== state.currentRepository?.id,
      )
      copilotLocalStorage.setCurrentReferences(state.selectedThreadID, referencesWithoutCurrentRepo)
    },
    [state.selectedThreadID, state.currentReferences, state.currentRepository, state.chatIsOpen],
  )

  return (
    <EntitlementProvider
      enabled={!deferEntitlementUntilOpen || state.chatIsOpen}
      initialLicenseType={copilotChatPayload.licenseType}
      initialPlan={copilotChatPayload.plan}
      initialQuotas={copilotChatPayload.quotas}
      hasCEorCBAccess={copilotChatPayload.hasCEorCBAccess}
      initialCanUpgradePlan={copilotChatPayload.canUpgradePlan}
    >
      <ChatStateProvider state={state}>
        <CopilotChatDispatchContext value={dispatch}>
          <CopilotChatManagerProvider
            apiURL={copilotChatPayload.apiURL}
            apiVersion={copilotChatPayload.apiVersion}
            state={state}
            dispatch={dispatch}
            ssoOrganizations={ssoOrganizations}
            realIp={realIp}
            hasCEorCBAccess={copilotChatPayload.hasCEorCBAccess}
            enableV1CitationMarkerSanitization={enableV1CitationMarkerSanitization}
            waitForInitialInstructionRules={waitForInitialInstructionRules}
            instructionRulesLoadingFallback={instructionRulesLoadingFallback}
          >
            <CopilotSpaceSelectionProvider selectedThreadId={state.selectedThreadID}>
              {children}
            </CopilotSpaceSelectionProvider>
          </CopilotChatManagerProvider>
        </CopilotChatDispatchContext>
      </ChatStateProvider>
    </EntitlementProvider>
  )
}

export function ChatStateProvider({state, children}: PropsWithChildren<{state: CopilotChatState}>) {
  // A constant reference to an Observable
  const [stateObservable, setStateObservable] = useObservableValueRef(state)

  // When the state updates, update the value of the observable
  useLayoutEffect(() => {
    if (state !== stateObservable.current.value) {
      setStateObservable(state)
    }
  }, [state, stateObservable, setStateObservable])

  return (
    <ObservableChatStateProvider value={stateObservable}>
      <ChatStateContext value={state}>{children}</ChatStateContext>
    </ObservableChatStateProvider>
  )
}

/**
 * Like {@link useChatStateLens}, but usable outside a `CopilotChatProvider`, where the lens receives
 * `undefined`. Keeps the selective subscription, so consumers do not re-render on unrelated chat
 * updates the way a full-state read would.
 */
export function useSafeChatStateLens<T>(lens: (state: CopilotChatState | undefined) => T): T {
  const o = useObservableChatStateContext()
  // ObservableValue is invariant in T, so widening the handle needs a cast. It is only ever read
  // here, and the widened handle is not exposed to callers.
  const source = (o?.current ?? emptyChatStateObservable) as ObservableValue<CopilotChatState | undefined>
  const valueObservable = useDerivedObservable(source, lens)
  return useObservedState(valueObservable)
}

/**
 * Returns the result of the lens function applied to the chat state.
 * This causes the component to re-render whenever the given function results in a new value.
 * The lens function must always be pure.
 *
 * @example
 * ```ts
 * const hasMessages = useChatStateLens(s => s.messages.length > 0)
 * ```
 */
export function useChatStateLens<T>(lens: (state: CopilotChatState) => T): T {
  const o = useObservableChatStateContext()
  if (!o || !o.current) throw new Error('useChatStateLens can only be used inside a CopilotChatProvider')
  const valueObservable = useDerivedObservable(o.current, lens)
  return useObservedState(valueObservable)
}

/**
 * Returns the current value of `state[key]` on the `CopilotChatState`.
 * This causes the component to re-render whenever that property has a new value.
 *
 * @example
 * ```ts
 * const model = useChatStateValue('model')
 * ```
 */
export function useChatStateValue<T extends keyof CopilotChatState>(key: T): CopilotChatState[T] {
  return useChatStateLens(s => s[key])
}

/**
 * Returns a subset of the `CopilotChatState` containing only the specified keys.
 * This causes the component to re-render whenever a property corresponding to one of those keys has a new value.
 *
 * @example
 * ```ts
 * const {model, availableModels} = useChatStateValues('model', 'availableModels')
 * ```
 */
export function useChatStateValues<TKey extends keyof CopilotChatState = never>(
  ...keys: TKey[]
): Pick<CopilotChatState, TKey> {
  return useChatStateLens(s => getSubsetFromState(s, keys))
}

export function getSubsetFromState<TObject extends object, TKey extends keyof TObject>(
  state: TObject,
  keys: TKey[],
): Pick<TObject, TKey> {
  const out = {} as unknown as Pick<TObject, TKey>
  for (const key of keys) {
    out[key] = state[key]
  }
  return out
}

export function useChatDispatch(): React.Dispatch<CopilotChatAction> {
  const dispatch = use(CopilotChatDispatchContext)
  if (!dispatch) throw new Error('useChatDispatch can only be used inside a CopilotChatProvider')
  return dispatch
}
